import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../static/api';
import { getFileFromResponse } from '../../../utils/api/getFileFromResponse/getFileFromResponse';
import {
  ZipProgress,
  isClientZipSupported,
  streamGameMediaZipToDisk,
} from '../../../utils/api/downloadGameMediaZip/downloadGameMediaZip';
import useAxiosInstance from '../../axios/useAxiosInstance';
import useToast from '../../useToast';

type MediaType = 'images' | 'videos';

const MEDIA_CONFIG: Record<
  MediaType,
  { endpoint: (arg: { gameId: number }) => string; label: string; extension: string }
> = {
  images: { endpoint: api.endpoints.GAME_IMAGES, label: 'image', extension: 'jpg' },
  videos: { endpoint: api.endpoints.GAME_VIDEOS, label: 'video', extension: 'mp4' },
};

// GMT-797 (Porcja 2): adaptacyjny eksport mediów gry.
// - Chrome/Edge (File System Access API) → streaming ZIP prosto na dysk (bez OOM,
//   dowolny rozmiar) z postępem plik-po-pliku.
// - inne przeglądarki → backendowy ZIP (blob); gdy gra za duża, backend zwraca
//   413 i pokazujemy komunikat "użyj Chrome".
const useGameMediaDownload = (mediaType: MediaType) => {
  const { t } = useTranslation();
  const axiosInstance = useAxiosInstance();
  const { showErrorToast, showSuccessToast } = useToast();

  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState<ZipProgress | null>(null);

  const config = MEDIA_CONFIG[mediaType];

  const downloadViaBackend = async (gameId: number) => {
    try {
      const response = await axiosInstance.get(config.endpoint({ gameId }), {
        responseType: 'arraybuffer',
      });

      getFileFromResponse({
        response,
        fileContentType: 'application/zip',
        fileType: 'zip',
        fileName: `game_${mediaType}_${gameId}`,
      });

      showSuccessToast(t(`successMessages.${mediaType}Downloaded`));
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 413) {
        showErrorToast(t('errorMessages.mediaExportTooLarge'));
      } else {
        showErrorToast(t(`errorMessages.${mediaType}DownloadError`));
      }
    }
  };

  const downloadViaClient = async (gameId: number) => {
    const { data } = await axiosInstance.get(api.endpoints.GAME_MEDIA({ gameId }));
    const urls: string[] = data?.[mediaType] ?? [];

    if (urls.length === 0) {
      showErrorToast(t('errorMessages.mediaExportEmpty'));
      return;
    }

    setProgress({ done: 0, total: urls.length, skipped: 0 });

    const saved = await streamGameMediaZipToDisk({
      urls,
      suggestedName: `game_${mediaType}_${gameId}.zip`,
      mediaLabel: config.label,
      extension: config.extension,
      onProgress: setProgress,
    });

    if (saved) {
      showSuccessToast(t(`successMessages.${mediaType}Downloaded`));
    }
  };

  const handleDownload = async ({ gameId }: { gameId: number }) => {
    setIsLoading(true);

    try {
      if (isClientZipSupported()) {
        await downloadViaClient(gameId);
      } else {
        await downloadViaBackend(gameId);
      }
    } catch {
      showErrorToast(t(`errorMessages.${mediaType}DownloadError`));
    } finally {
      setIsLoading(false);
      setProgress(null);
    }
  };

  return { handleDownload, isLoading, progress };
};

export default useGameMediaDownload;
